import { Injectable } from '@angular/core';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { UserDAO } from './dao/userDAO';
import { VesselRateDAO } from './dao/vesselRateDAO';
import { LineItem } from 'anchor-shared/lib/model/interface/buyflowProductOffering';
import { RoundDownPipe } from '../../app/shared/pipes/roundDown.pipe';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import { roundToNearestCent } from 'anchor-shared/lib/service/utils/helper';

@Injectable()
export class VesselRateCalculationService {
  constructor(
    private userDao: UserDAO,
    private vesselRateDao: VesselRateDAO,
    private roundDownPipe: RoundDownPipe,
  ) {}

  public getListPrice(vesselRates: VesselRate[], duration: number): number {
    if (duration === 4) {
      return this.getHalfDayListPriceTotal(vesselRates);
    } else if (duration === 8) {
      return this.getFullDayListPriceTotal(vesselRates);
    } else {
      return this.getHourlyListPriceTotal(vesselRates, duration);
    }
  }

  rateTypeIncludedInListPrice(rateType: RateType): boolean {
    return (
      rateType === RateType.Captain ||
      rateType === RateType.Owner ||
      rateType === RateType.Crew
    );
  }

  private getHalfDayListPriceTotal(vesselRates: VesselRate[]): number {
    let total = 0;

    vesselRates.forEach((rate) => {
      if (this.rateTypeIncludedInListPrice(rate.rateType)) {
        if (rate.halfDayRate) {
          total += rate.halfDayRate;
          if (rate.markupPercentage) {
            total += rate.halfDayRate * rate.markupPercentage;
          }
        }
      }
    });

    return roundToNearestCent(total);
  }

  private getFullDayListPriceTotal(vesselRates: VesselRate[]): number {
    let total = 0;

    vesselRates.forEach((rate) => {
      if (this.rateTypeIncludedInListPrice(rate.rateType)) {
        if (rate.fullDayRate) {
          total += rate.fullDayRate;
          if (rate.markupPercentage) {
            total += rate.fullDayRate * rate.markupPercentage;
          }
        }
      }
    });

    return roundToNearestCent(total);
  }

  private getHourlyListPriceTotal(
    vesselRates: VesselRate[],
    numberOfHours: number,
  ): number {
    let total = 0;

    vesselRates.forEach((rate) => {
      if (this.rateTypeIncludedInListPrice(rate.rateType)) {
        if (rate.hourlyRate) {
          total += rate.hourlyRate * numberOfHours;
          if (rate.markupPercentage) {
            total += rate.hourlyRate * numberOfHours * rate.markupPercentage;
          }
        }
      }
    });

    return roundToNearestCent(total);
  }

  public calculateAdditionalOneHourListPriceTotal(
    vesselRates: VesselRate[],
  ): number {
    let total = 0;
    vesselRates.forEach((rate) => {
      if (this.rateTypeIncludedInListPrice(rate.rateType)) {
        total += this.calculateHourlyWithMarkupAndTax(rate);
      }
    });
    return roundToNearestCent(total);
  }

  public calculateHourlyWithMarkupAndTax(rate: VesselRate): number {
    if (!rate || !rate.hourlyRate) {
      throw {
        message: 'Not an Hourly Rate',
      };
    }
    let runningTotal = 0;
    runningTotal += rate.hourlyRate;
    if (rate.markupPercentage) {
      runningTotal += rate.hourlyRate * rate.markupPercentage;
    }
    if (rate.taxPercentage) {
      runningTotal += runningTotal * rate.taxPercentage;
    }
    return runningTotal;
  }

  public getPayoutForOwner(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customDuration: number,
  ): number {
    if (rate.rateType !== RateType.Owner) {
      throw Error('Rate must be of type Owner.');
    }
    const payoutAmount = this.calculateVesselRate(
      rate,
      durationType,
      customDuration,
      false,
    );
    return payoutAmount + payoutAmount * rate.taxPercentage;
  }

  public getPayoutForCaptain(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customDuration: number,
  ): number {
    if (rate.rateType !== RateType.Captain) {
      throw Error('Rate must be of type Captain.');
    }
    return this.calculateVesselRate(rate, durationType, customDuration, false);
  }

  public getPayoutForMiscRate(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customDuration: number,
  ): number {
    if (!this.isCustomRate(rate)) {
      throw Error('Rate must be of type Custom or Custom (time based).');
    }
    return this.calculateVesselRate(rate, durationType, customDuration, false);
  }

  public getPayoutForCrewMember(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customDuration: number,
    useDefaultRate?: boolean,
  ): number {
    if (!rate || useDefaultRate) {
      return 15 * customDuration;
    }
    if (rate.rateType !== RateType.Crew) {
      throw Error('Rate must be of type Crew.');
    }
    return this.calculateVesselRate(rate, durationType, customDuration, false);
  }

  public getVesselRate(rates: VesselRate[], rateType: RateType): VesselRate {
    let returnRate = null;
    rates.forEach((rate) => {
      if (rate.rateType === rateType) {
        returnRate = rate;
      }
    });
    return returnRate;
  }

  /**
   * v2 methods below
   * we will get rid of the ones above that we don't need.
   */

  totalBaseCostForRates(
    rates: VesselRate[],
    durationType: 0 | 4 | 8,
    customDuration: number,
  ): number {
    let total = 0;
    rates.forEach((rate) => {
      let toBeAdded = this.calculateVesselRate(
        rate,
        durationType,
        customDuration,
        true,
      );

      // owner cost rate includes tax
      if (rate.rateType === RateType.Owner) {
        if (rate.taxPercentage) {
          toBeAdded += toBeAdded * rate.taxPercentage;
        }
      }
      total += toBeAdded;
    });

    return total;
  }

  calculateVesselRate(
    rate: VesselRate,
    durationType: number,
    customDuration: number,
    includeMarkup: boolean,
  ): number {
    let baseRate = 0;
    if ([RateType.Cleaning, RateType.Custom].includes(rate.rateType)) {
      baseRate = rate.customRate;
    } else {
      baseRate = this.getVesselRateForDurationType(
        rate,
        durationType,
        customDuration,
      );
    }
    if (includeMarkup) {
      baseRate = baseRate * (1 + rate.markupPercentage);
    }
    return baseRate;
  }

  calculateVesselRateProduct(
    lineRate,
    duration: number,
    includeMarkup: boolean,
  ) {
    let baseRate = lineRate.price;
    if (includeMarkup) {
      baseRate = baseRate * (1 + lineRate.markupPercentage);
    }
    return baseRate;
  }

  getVesselRateForDurationType(
    rate: VesselRate,
    durationType: number,
    customDuration: number,
  ): number {
    let returnPrice;
    if (durationType === 4) {
      // half day
      returnPrice = rate.halfDayRate;
    } else if (durationType === 8) {
      // full day
      returnPrice = rate.fullDayRate;
    } else if (customDuration > 0 && customDuration <= 4) {
      // custom <= half day
      returnPrice = this.getCustomHalfForRate(rate, customDuration);
    } else if (customDuration > 4 && customDuration <= 8) {
      // half day > custom <= full day
      returnPrice = this.getCustomFullForRate(rate, customDuration);
    } else if (customDuration > 8) {
      // custom > full day
      returnPrice = this.getCustomBeyondFullForRate(rate, customDuration);
    } else {
      returnPrice = 0;
    }
    return returnPrice;
  }

  getVesselRateTax(
    rates: VesselRate[],
    durationType: number,
    customDuration: number,
  ): number {
    let tax = 0;
    rates.forEach((rate) => {
      if (rate.taxPercentage > 0) {
        tax +=
          this.calculateVesselRate(rate, durationType, customDuration, true) *
          rate.taxPercentage;
      }
    });
    return tax;
  }

  getVesselRateProductTax(lineRates: LineItem[]): number {
    let tax = 0;
    lineRates.forEach((rate) => {
      tax += rate.estimatedTax;
    });
    return tax;
  }

  getVesselRateTotal(
    rates: VesselRate[],
    currentPrice: number,
    durationType: number,
    customDuration: number,
    fuelIncluded?: boolean,
  ): number {
    let total = currentPrice;
    rates.forEach((rate) => {
      if (
        this.isCustomRate(rate) ||
        rate.rateType === RateType.BookingFee ||
        rate.rateType === RateType.Gratuity ||
        rate.rateType === RateType.Cleaning
      ) {
        total += this.calculateVesselRate(
          rate,
          durationType,
          customDuration,
          true,
        );
      }
    });
    if (fuelIncluded && this.getFuelIncludedRate(rates)) {
      total += this.calculateVesselRate(
        this.getFuelIncludedRate(rates),
        durationType,
        customDuration,
        true,
      );
    }
    total += this.getVesselRateTax(rates, durationType, customDuration);

    return total;
  }

  getVesselRateProductTotal(
    lineItem: LineItem[],
    duration: number,
    vesselRates: VesselRate[],
  ): number {
    let total = 0;
    let ownerPrice = 0;
    vesselRates.find((rate) => {
      if (rate.rateType === 'Owner') {
        if (duration === 4) {
          ownerPrice = rate.halfDayRate;
        } else if (duration === 8) {
          ownerPrice = rate.fullDayRate;
        } else if (duration < 4 && duration > 0) {
          // less then 3 hours
          let additionalHours = 4 - duration;
          ownerPrice = rate.halfDayRate - rate.hourlyRate * additionalHours;
        } else if (duration > 4 && duration < 8) {
          // more halfDayRate 4 less fullDayRate 8
          let additionalHours = duration - 4;
          ownerPrice = rate.halfDayRate + rate.hourlyRate * additionalHours;
        } else if (duration > 8) {
          // more then fullDayRate
          let additionalHours = duration - 8;
          ownerPrice = rate.fullDayRate + rate.hourlyRate * additionalHours;
        }
        ownerPrice = parseFloat(ownerPrice.toFixed(2));
        ownerPrice = parseFloat(
          (
            ownerPrice *
            (1 + rate.markupPercentage) *
            (1 + rate.taxPercentage)
          ).toFixed(2),
        );

        total += ownerPrice;
      }
    });
    lineItem.forEach((item) => {
      if (item.lineItemName === 'Owner') {
        total = total;
      } else {
        total += item.price;
      }
    });
    return total;
  }

  getVesselRatePriceAdjustedTotal(lineItem: LineItem[]): number {
    let total = 0;
    lineItem.forEach((item) => {
      total += item.price;
    });
    total += this.getVesselRateProductTax(lineItem);
    return total;
  }

  getBoatCaptainCrewLineItemsTotal(lineItems: LineItem[]): number {
    let total = 0;
    lineItems.forEach((item) => {
      total += item.price;
    });
    return total;
  }

  getBoatCaptainCrewLineItemsOriginalTotal(lineItems: LineItem[]): string {
    let total = 0;
    lineItems.forEach((item) => {
      total += item.originalPrice;
    });
    return `$${this.roundDownPipe.transform(total).toString()}`;
  }

  isCustomRate(rate: VesselRate): boolean {
    return (
      rate.rateType === RateType.Custom ||
      rate.rateType === RateType.CustomTimeBased
    );
  }

  getCustomHalfForRate(rate: VesselRate, customDuration: number) {
    return parseFloat(
      (rate.halfDayRate - rate.hourlyRate * (4 - customDuration)).toFixed(2),
    );
  }

  getCustomFullForRate(rate: VesselRate, customDuration: number) {
    return parseFloat(
      (rate.halfDayRate + rate.hourlyRate * (customDuration - 4)).toFixed(2),
    );
  }

  getCustomBeyondFullForRate(rate: VesselRate, customDuration: number) {
    return parseFloat(
      (rate.fullDayRate + rate.hourlyRate * (customDuration - 8)).toFixed(2),
    );
  }

  getCustomHalfForVessel(vessel: Vessel, customDuration: number) {
    return parseFloat(
      (vessel.additionalHoursListPrice * customDuration).toFixed(2),
    );
  }

  getCustomFullForVessel(vessel: Vessel, customDuration: number) {
    return parseFloat(
      (
        vessel.halfDayListPrice +
        vessel.additionalHoursListPrice * (customDuration - 4)
      ).toFixed(2),
    );
  }

  getCustomBeyondFullForVessel(vessel: Vessel, customDuration: number) {
    return parseFloat(
      (
        vessel.fullDayListPrice +
        vessel.additionalHoursListPrice * (customDuration - 8)
      ).toFixed(2),
    );
  }

  getBoatCaptainCrewTotal(
    vessel: Vessel,
    durationType: number,
    customDuration: number,
  ): number {
    let returnPrice;
    if (durationType === 4) {
      // half day
      returnPrice = vessel.halfDayListPrice;
    } else if (durationType === 8) {
      // full day
      returnPrice = vessel.fullDayListPrice;
    } else if (customDuration > 0 && customDuration <= 4) {
      // custom <= half day
      returnPrice = this.getCustomHalfForVessel(vessel, customDuration);
    } else if (customDuration > 4 && customDuration <= 8) {
      // half day > custom <= full day
      returnPrice = this.getCustomFullForVessel(vessel, customDuration);
    } else if (customDuration > 8) {
      // custom > full day
      returnPrice = this.getCustomBeyondFullForVessel(vessel, customDuration);
    } else {
      returnPrice = 0;
    }
    return returnPrice;
  }

  public getMiscRate(
    vesselRates: VesselRate[],
    payoutName: string,
  ): VesselRate {
    return vesselRates.find((element) => element.name === payoutName);
  }

  public calculateCostFromVesselRate(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customNumberOfHours: number,
  ): number {
    let toBeAdded = this.calculateVesselRate(
      rate,
      durationType,
      customNumberOfHours,
      false,
    );

    // owner cost rate includes tax
    if (rate.rateType === RateType.Owner && rate.taxPercentage > 0) {
      toBeAdded += toBeAdded * rate.taxPercentage;
    }

    return toBeAdded;
  }

  public getFuelIncludedRate(rates: VesselRate[]): VesselRate {
    let fuelRate = null;
    rates.forEach((rate) => {
      if (rate.rateType === RateType.FuelIncluded) {
        fuelRate = rate;
      }
    });
    return fuelRate;
  }

  public getPayoutForFuel(
    rate: VesselRate,
    durationType: 0 | 4 | 8,
    customDuration: number,
  ): number {
    if (!rate) {
      return 0;
    }
    if (rate.rateType !== RateType.FuelIncluded) {
      throw Error('Rate must be of type Fuel.');
    }
    return this.calculateVesselRate(rate, durationType, customDuration, false);
  }
}
